import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, CheckCircle2, ChevronDown, Lock, Play, RotateCcw, Sparkles } from 'lucide-react';
import type { CourseProgress } from '@formency/shared';
import type { ModuleWithContent } from '@/shared/api/hooks/useCourses';
import { Badge, Button, Select, Surface } from '@/interfaces/learner/ui';

type LessonStatus = CourseProgress['lessons'][number]['status'];
type TopicStatus = 'completed' | 'active' | 'locked';
type ModuleStatus = 'completed' | 'active' | 'locked';

interface CourseCurriculumAccordionProps {
  modules: ModuleWithContent[];
  progress: CourseProgress | undefined;
  selectedModuleId: string | null;
  onSelectModule: (moduleId: string | null) => void;
  activeModuleId?: string;
  activeTopicId?: string;
  onOpenLesson: (lessonId: string) => void;
}

interface LessonItem {
  id: string;
  title: string;
  status: LessonStatus;
  scorePct: number | null;
  indexInTopic: number;
}

interface TopicItem {
  id: string;
  title: string;
  description?: string | null;
  lessons: LessonItem[];
  totalLessons: number;
  completedLessons: number;
  status: TopicStatus;
}

interface ModuleItem {
  id: string;
  title: string;
  description?: string | null;
  topics: TopicItem[];
  totalLessons: number;
  completedLessons: number;
  status: ModuleStatus;
}

const EASE = [0.16, 1, 0.3, 1] as const;

export default function CourseCurriculumAccordion({
  modules,
  progress,
  selectedModuleId,
  onSelectModule,
  activeModuleId,
  activeTopicId,
  onOpenLesson,
}: CourseCurriculumAccordionProps) {
  const moduleItems = useMemo(() => buildModuleItems(modules, progress), [modules, progress]);

  const visibleModules = useMemo(
    () =>
      selectedModuleId
        ? moduleItems.filter((moduleItem) => moduleItem.id === selectedModuleId)
        : moduleItems,
    [moduleItems, selectedModuleId]
  );

  const [expandedModuleIdOverride, setExpandedModuleIdOverride] = useState<string | null>(null);
  const [expandedTopicByModule, setExpandedTopicByModule] = useState<Record<string, string>>({});

  const expandedModuleId = useMemo(() => {
    if (visibleModules.length === 0) return null;

    const visibleIds = new Set(visibleModules.map((moduleItem) => moduleItem.id));
    if (selectedModuleId && visibleIds.has(selectedModuleId)) return selectedModuleId;
    if (expandedModuleIdOverride && visibleIds.has(expandedModuleIdOverride))
      return expandedModuleIdOverride;
    if (activeModuleId && visibleIds.has(activeModuleId)) return activeModuleId;
    return visibleModules[0].id;
  }, [visibleModules, selectedModuleId, expandedModuleIdOverride, activeModuleId]);

  if (moduleItems.length === 0) {
    return (
      <Surface className="mt-8" padding={22}>
        <p className="text-body-muted">No published lessons are available for this course yet.</p>
      </Surface>
    );
  }

  return (
    <section
      id="course-curriculum"
      className="mt-8 learner-double:grid learner-double:grid-cols-[260px_minmax(0,1fr)] learner-double:gap-8"
    >
      <nav className="hidden learner-double:block" aria-label="Course modules">
        <Surface
          className="sticky"
          style={{
            top: 'var(--sticky-top-offset)',
            maxHeight: 'calc(100vh - var(--navbar-height) - 40px)',
            overflowY: 'auto',
          }}
          padding={12}
        >
          <p className="text-eyebrow px-2 pb-2" style={{ color: 'var(--color-text-muted)' }}>
            Modules
          </p>

          <div className="space-y-1">
            <button
              type="button"
              onClick={() => onSelectModule(null)}
              className="text-list-title w-full rounded-xl px-3 py-2 text-left transition-colors"
              style={{
                color: selectedModuleId === null ? 'var(--color-text)' : 'var(--color-text-muted)',
                background:
                  selectedModuleId === null ? 'var(--color-accent-subtle)' : 'transparent',
                border:
                  selectedModuleId === null
                    ? '1px solid var(--color-surface-border)'
                    : '1px solid transparent',
              }}
            >
              All modules
            </button>

            {moduleItems.map((moduleItem) => {
              const isSelected = selectedModuleId === moduleItem.id;
              const isCurrent = activeModuleId === moduleItem.id;

              return (
                <button
                  key={moduleItem.id}
                  type="button"
                  onClick={() => {
                    onSelectModule(moduleItem.id);
                    setExpandedModuleIdOverride(moduleItem.id);
                  }}
                  className="w-full rounded-xl px-3 py-2 text-left transition-colors"
                  style={{
                    border: isSelected
                      ? '1px solid var(--color-surface-border)'
                      : '1px solid transparent',
                    background: isSelected ? 'var(--color-surface-solid)' : 'transparent',
                  }}
                >
                  <span
                    className="text-list-title block truncate"
                    style={{
                      color: isSelected ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {moduleItem.title}
                  </span>
                  <span
                    className="text-list-meta mt-0.5 block"
                    style={{
                      color: isCurrent ? 'var(--color-accent-text)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {moduleItem.completedLessons}/{moduleItem.totalLessons} lessons
                  </span>
                </button>
              );
            })}
          </div>
        </Surface>
      </nav>

      <div>
        {moduleItems.length > 1 && (
          <div className="mb-4 learner-double:hidden">
            <Select
              id="course-module-select"
              label="Module"
              size="sm"
              selectedKey={selectedModuleId ?? '__all__'}
              onSelectionChange={(key) => {
                const value = String(key);
                onSelectModule(value === '__all__' ? null : value);
              }}
              style={{
                minWidth: 220,
                maxWidth: 320,
              }}
              options={[
                { value: '__all__', label: 'All modules' },
                ...moduleItems.map((moduleItem) => ({
                  value: moduleItem.id,
                  label: moduleItem.title,
                })),
              ]}
            />
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={selectedModuleId === null ? 'primary' : 'secondary'}
            onPress={() => onSelectModule(null)}
          >
            Show all modules
          </Button>
          {activeModuleId && (
            <Button
              size="sm"
              variant={selectedModuleId === activeModuleId ? 'primary' : 'secondary'}
              onPress={() => onSelectModule(activeModuleId)}
            >
              Focus current module
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {visibleModules.map((moduleItem, index) => {
            const isExpanded = expandedModuleId === moduleItem.id;
            const expandedTopicId = expandedTopicByModule[moduleItem.id] ?? '';
            const resolvedTopicId =
              expandedTopicId && moduleItem.topics.some((topic) => topic.id === expandedTopicId)
                ? expandedTopicId
                : getDefaultTopicId(moduleItem, activeModuleId, activeTopicId);
            const moduleBadgeVariant = statusToBadgeVariant(moduleItem.status);

            return (
              <motion.div
                key={moduleItem.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: index * 0.03, ease: EASE }}
              >
                <Surface
                  style={{
                    overflow: 'hidden',
                    background:
                      moduleItem.id === activeModuleId || isExpanded
                        ? 'var(--color-accent-wash-strong)'
                        : undefined,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedModuleIdOverride(moduleItem.id)}
                    className="w-full text-left"
                    style={{
                      padding: '16px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-[10px]"
                      style={{
                        background:
                          moduleItem.status === 'completed'
                            ? 'var(--color-success-subtle)'
                            : moduleItem.status === 'active'
                              ? 'var(--color-accent-subtle)'
                              : 'var(--color-surface-solid)',
                      }}
                    >
                      {moduleItem.status === 'completed' ? (
                        <CheckCircle2 size={16} style={{ color: 'var(--color-success)' }} />
                      ) : moduleItem.status === 'active' ? (
                        <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
                      ) : (
                        <Lock size={16} style={{ color: 'var(--color-text-muted)' }} />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p
                        className="text-card-title truncate"
                        style={{
                          color: 'var(--color-text)',
                        }}
                      >
                        {moduleItem.title}
                      </p>
                      <p className="text-list-meta" style={{ marginTop: 3 }}>
                        {moduleItem.completedLessons}/{moduleItem.totalLessons} lessons complete
                      </p>
                      {moduleItem.description && (
                        <p className="text-list-meta mt-1 line-clamp-1">{moduleItem.description}</p>
                      )}
                    </div>

                    <Badge variant={moduleBadgeVariant}>
                      {moduleStatusLabel(moduleItem.status)}
                    </Badge>
                    <ChevronDown
                      size={18}
                      style={{
                        color: 'var(--color-text-muted)',
                        transform: isExpanded ? 'rotate(180deg)' : undefined,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.26, ease: EASE }}
                        style={{
                          borderTop: '1px solid var(--color-surface-border)',
                          background: 'transparent',
                        }}
                      >
                        <div className="px-3 py-2">
                          {moduleItem.topics.map((topicItem, topicIndex) => {
                            const topicOpen = resolvedTopicId === topicItem.id;
                            const topicBadgeVariant = statusToBadgeVariant(topicItem.status);

                            return (
                              <div
                                key={topicItem.id}
                                style={{
                                  borderTop:
                                    topicIndex === 0
                                      ? 'none'
                                      : '1px solid var(--color-surface-border)',
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedTopicByModule((prev) => ({
                                      ...prev,
                                      [moduleItem.id]:
                                        prev[moduleItem.id] === topicItem.id ? '' : topicItem.id,
                                    }))
                                  }
                                  className="w-full"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '13px 8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    background: 'transparent',
                                    textAlign: 'left',
                                  }}
                                >
                                  <ChevronDown
                                    size={16}
                                    style={{
                                      color: 'var(--color-text-muted)',
                                      transform: topicOpen ? 'rotate(180deg)' : undefined,
                                      transition: 'transform 0.2s',
                                    }}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-list-title truncate">{topicItem.title}</p>
                                    <p className="text-list-meta" style={{ marginTop: 2 }}>
                                      {topicItem.completedLessons}/{topicItem.totalLessons} lessons
                                      complete
                                    </p>
                                    {topicItem.description && (
                                      <p className="text-list-meta mt-1 line-clamp-1">
                                        {topicItem.description}
                                      </p>
                                    )}
                                  </div>
                                  <Badge variant={topicBadgeVariant}>
                                    {topicStatusLabel(topicItem.status)}
                                  </Badge>
                                </button>

                                <AnimatePresence initial={false}>
                                  {topicOpen && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.22, ease: EASE }}
                                    >
                                      <div className="pb-3 pr-2 pl-9">
                                        <div className="relative mt-1">
                                          <div
                                            className="pointer-events-none absolute top-1 bottom-1 left-[14px] w-px"
                                            style={{
                                              background: 'var(--color-accent-line)',
                                            }}
                                          />

                                          <div className="space-y-1">
                                            {topicItem.lessons.map((lessonItem) => {
                                              const isLocked = lessonItem.status === 'locked';
                                              const statusLabel = lessonTrailing(lessonItem);
                                              const markerStyle = markerStyles(lessonItem.status);

                                              const row = (
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    width: '100%',
                                                    padding: '8px 6px 8px 0',
                                                    borderRadius: 10,
                                                    textAlign: 'left',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: isLocked ? 'default' : 'pointer',
                                                  }}
                                                >
                                                  <span
                                                    className="relative z-10 flex h-7 w-7 items-center justify-center rounded-full"
                                                    style={markerStyle}
                                                  >
                                                    {lessonItem.status === 'completed' ? (
                                                      <Check size={12} />
                                                    ) : lessonItem.status === 'in-progress' ? (
                                                      <Play size={11} />
                                                    ) : lessonItem.status === 'failed' ? (
                                                      <RotateCcw size={11} />
                                                    ) : (
                                                      <span
                                                        className="text-list-meta tabular-nums"
                                                        style={{
                                                          fontWeight: 'var(--weight-strong)',
                                                        }}
                                                      >
                                                        {lessonItem.indexInTopic}
                                                      </span>
                                                    )}
                                                  </span>

                                                  <div className="min-w-0 flex-1">
                                                    <p
                                                      className="text-list-title truncate"
                                                      style={{
                                                        color: isLocked
                                                          ? 'var(--color-text-muted)'
                                                          : 'var(--color-text)',
                                                        fontWeight: isLocked
                                                          ? 'var(--weight-medium)'
                                                          : 'var(--weight-semibold)',
                                                      }}
                                                    >
                                                      {lessonItem.title}
                                                    </p>
                                                  </div>

                                                  <span
                                                    className="text-list-meta"
                                                    style={{
                                                      color: statusColor(lessonItem.status),
                                                    }}
                                                  >
                                                    {statusLabel}
                                                  </span>
                                                </div>
                                              );

                                              if (isLocked) {
                                                return <div key={lessonItem.id}>{row}</div>;
                                              }

                                              return (
                                                <button
                                                  key={lessonItem.id}
                                                  type="button"
                                                  onClick={() => onOpenLesson(lessonItem.id)}
                                                  className="w-full rounded-[10px] transition-colors"
                                                  onMouseEnter={(event) => {
                                                    event.currentTarget.style.background =
                                                      'var(--color-accent-wash)';
                                                  }}
                                                  onMouseLeave={(event) => {
                                                    event.currentTarget.style.background =
                                                      'transparent';
                                                  }}
                                                >
                                                  {row}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Surface>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function buildModuleItems(
  modules: ModuleWithContent[],
  progress: CourseProgress | undefined
): ModuleItem[] {
  const progressMap = new Map(
    progress?.lessons.map((lessonProgress) => [lessonProgress.lessonId, lessonProgress])
  );
  const hasProgressData = progressMap.size > 0;
  let usedFallbackAvailable = false;

  const moduleItems: ModuleItem[] = [];

  for (const module of modules) {
    const topics = module.topics ?? [];
    const topicItems: TopicItem[] = [];

    for (const topic of topics) {
      const lessons = topic.lessons ?? [];
      if (lessons.length === 0) continue;

      const lessonItems: LessonItem[] = lessons.map((lesson, lessonIndex) => {
        const lessonProgress = progressMap.get(lesson.id);
        let status: LessonStatus;

        if (lessonProgress) {
          status = lessonProgress.status;
        } else if (!hasProgressData && !usedFallbackAvailable) {
          status = 'available';
          usedFallbackAvailable = true;
        } else {
          status = 'locked';
        }

        return {
          id: lesson.id,
          title: lesson.title,
          status,
          scorePct: lessonProgress?.scorePct ?? null,
          indexInTopic: lessonIndex + 1,
        };
      });

      const completedLessons = lessonItems.filter(
        (lessonItem) => lessonItem.status === 'completed'
      ).length;
      const hasUnlocked = lessonItems.some((lessonItem) => lessonItem.status !== 'locked');

      const topicStatus: TopicStatus =
        completedLessons === lessonItems.length ? 'completed' : hasUnlocked ? 'active' : 'locked';

      topicItems.push({
        id: topic.id,
        title: topic.title,
        description: topic.description ?? null,
        lessons: lessonItems,
        totalLessons: lessonItems.length,
        completedLessons,
        status: topicStatus,
      });
    }

    if (topicItems.length === 0) continue;

    const allLessons = topicItems.flatMap((topicItem) => topicItem.lessons);
    const completedLessons = allLessons.filter(
      (lessonItem) => lessonItem.status === 'completed'
    ).length;
    const hasUnlocked = allLessons.some((lessonItem) => lessonItem.status !== 'locked');

    const moduleStatus: ModuleStatus =
      completedLessons === allLessons.length ? 'completed' : hasUnlocked ? 'active' : 'locked';

    moduleItems.push({
      id: module.id,
      title: module.title,
      description: module.description ?? null,
      topics: topicItems,
      totalLessons: allLessons.length,
      completedLessons,
      status: moduleStatus,
    });
  }

  return moduleItems;
}

function getDefaultTopicId(
  moduleItem: ModuleItem,
  activeModuleId: string | undefined,
  activeTopicId: string | undefined
): string {
  if (moduleItem.id === activeModuleId && activeTopicId) {
    const match = moduleItem.topics.find((topicItem) => topicItem.id === activeTopicId);
    if (match) return match.id;
  }

  return (
    moduleItem.topics.find((topicItem) => topicItem.status === 'active')?.id ??
    moduleItem.topics[0]?.id ??
    ''
  );
}

function statusColor(status: LessonStatus): string {
  if (status === 'completed') return 'var(--color-success)';
  if (status === 'in-progress') return 'var(--color-accent)';
  if (status === 'failed') return 'var(--color-warning)';
  if (status === 'available') return 'var(--color-info)';
  return 'var(--color-text-muted)';
}

function markerStyles(status: LessonStatus): React.CSSProperties {
  if (status === 'completed') {
    return {
      color: 'var(--color-success)',
      background: 'var(--color-success-subtle)',
      border: '1px solid var(--color-success-outline)',
    };
  }
  if (status === 'in-progress') {
    return {
      color: 'var(--color-accent)',
      background: 'var(--color-accent-subtle)',
      border: '1px solid var(--color-accent-outline)',
    };
  }
  if (status === 'failed') {
    return {
      color: 'var(--color-warning)',
      background: 'var(--color-warning-subtle)',
      border: '1px solid var(--color-warning-outline)',
    };
  }
  if (status === 'available') {
    return {
      color: 'var(--color-info)',
      background: 'var(--color-info-subtle)',
      border: '1px solid var(--color-info-outline)',
    };
  }
  return {
    color: 'var(--color-text-muted)',
    background: 'var(--color-surface-solid)',
    border: '1px solid var(--color-surface-border)',
  };
}

function lessonTrailing(lessonItem: LessonItem): string | undefined {
  if (lessonItem.status === 'completed' && lessonItem.scorePct != null) {
    return `${Math.round(lessonItem.scorePct)}%`;
  }
  if (lessonItem.status === 'in-progress') return 'Resume';
  if (lessonItem.status === 'failed') return 'Retry';
  if (lessonItem.status === 'available') return 'Start';
  return 'Locked';
}

function statusToBadgeVariant(
  status: TopicStatus | ModuleStatus
): 'success' | 'accent' | 'warning' {
  if (status === 'completed') return 'success';
  if (status === 'active') return 'accent';
  return 'warning';
}

function moduleStatusLabel(status: ModuleStatus): string {
  if (status === 'completed') return 'Completed';
  if (status === 'active') return 'Current';
  return 'Locked';
}

function topicStatusLabel(status: TopicStatus): string {
  if (status === 'completed') return 'Done';
  if (status === 'active') return 'In path';
  return 'Locked';
}
